import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { AreaMaterial, CreateAreaMaterial, UpdateAreaMaterial } from '@estimator/shared';

export function useAreaMaterials(areaId: string | undefined) {
  return useQuery({
    queryKey: ['area-materials', areaId],
    queryFn: async () => {
      const { data } = await apiClient.get<AreaMaterial[]>('/area-materials', {
        params: { area_id: areaId },
      });
      return data;
    },
    enabled: !!areaId,
  });
}

export function useCreateAreaMaterial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateAreaMaterial) => {
      const { data } = await apiClient.post<AreaMaterial>('/area-materials', body);
      return data;
    },
    onSuccess: (_data, vars) =>
      queryClient.invalidateQueries({ queryKey: ['area-materials', vars.area_id] }),
  });
}

export function useUpdateAreaMaterial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateAreaMaterial & { id: string }) => {
      const { data } = await apiClient.patch<AreaMaterial>(`/area-materials/${id}`, body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['area-materials'] }),
  });
}

export function useDeleteAreaMaterial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/area-materials/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['area-materials'] }),
  });
}
